// Renders cities as voxel buildings with influence rings.
// Building kind selected by tier and faction. HP bar floats above.
//
// P0.1-full day 2 afternoon: the city-center buildings are batched via
// VoxelBuildingBatch — one InstancedMesh per prefab-kind (village/town/capital)
// instead of one VoxelBuilding per city. Overlays (HP bar, influence ring,
// moat, selection ring, completion VFX) stay per-city since they're small,
// visually distinct, and often unique per city.
//
// See docs/superpowers/plans/2026-06-23-voxel-batching-p0_1-full.md.

import type { SceneNode } from '@aod/protocol';
import { cityCentreKind, resolveBuildingPrefabId, styleForCulture } from '@aod/ui/prefabs';
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { cityOwnerId } from '../ownership.js';
import { HEX_HEIGHT, HEX_SIZE } from './HexGrid.js';
import { RenderLayer } from './RenderLayer.js';
import { VoxelBuildingBatch } from './VoxelBuildingBatch.js';
import type { BatchedBuilding } from './VoxelBuildingBatch.js';
import { tileSurfaceY } from './elevation.js';

interface CitiesProps {
  readonly cities: ReadonlyArray<SceneNode>;
  readonly onClick?: (city: SceneNode) => void;
  readonly selectedId?: string | null;
  /** When given, each city sits on its tile's real plateau height. */
  readonly biomeMap?: Map<string, string>;
}

/**
 * How far a city's tile plateau sits above the old fixed surface
 * (HEX_HEIGHT / 2) that the offsets below were tuned against.
 */
function cityLift(city: SceneNode, biomeMap?: Map<string, string>): number {
  const q = city.props?.q as number | undefined;
  const r = city.props?.r as number | undefined;
  if (!biomeMap || q === undefined || r === undefined) return 0;
  return tileSurfaceY(q, r, biomeMap) - HEX_HEIGHT / 2;
}

interface CompletionVfx {
  id: number;
  x: number;
  z: number;
  buildingName: string;
}

let nextVfxId = 0;

const CITY_BASE_Y = HEX_HEIGHT / 2 + 0.05;

export function VoxelCities({ cities, onClick, selectedId, biomeMap }: CitiesProps) {
  const [completionVfx, setCompletionVfx] = useState<ReadonlyArray<CompletionVfx>>([]);
  const prevBuildingsRef = useRef<Map<string, string[]>>(new Map());

  // Detect building completions by watching completedBuildings per city.
  useEffect(() => {
    const newVfx: CompletionVfx[] = [];
    for (const city of cities) {
      const current = (city.props?.completedBuildings as string[] | undefined) ?? [];
      const prev = prevBuildingsRef.current.get(city.id) ?? [];
      const added = current.filter((b) => !prev.includes(b));
      if (added.length > 0) {
        const [x, , z] = city.transform.position;
        for (const name of added) {
          newVfx.push({ id: nextVfxId++, x, z, buildingName: name });
        }
      }
      prevBuildingsRef.current.set(city.id, current);
    }
    if (newVfx.length > 0) {
      setCompletionVfx((prev) => [...prev, ...newVfx]);
      for (const v of newVfx) {
        setTimeout(() => {
          setCompletionVfx((cur) => cur.filter((d) => d.id !== v.id));
        }, 1200);
      }
    }
  }, [cities]);

  // Group cities by city-center prefab-kind (village/town/capital).
  const batches = useMemo<Array<{ prefabId: string; entries: BatchedBuilding[] }>>(() => {
    const groups = new Map<string, BatchedBuilding[]>();
    for (const city of cities) {
      const tier = (city.props?.tier as number | undefined) ?? 1;
      // Player type: the owner's culture picks the building style (unowned -> shared prefabs).
      const style =
        cityOwnerId(city.props) === undefined ? 'neutral' : styleForCulture(city.props?.culture);
      const prefabId = resolveBuildingPrefabId(cityCentreKind(tier), style);
      const [x, , z] = city.transform.position;
      const ownerId = cityOwnerId(city.props);
      const entry: BatchedBuilding = {
        id: city.id,
        ownerId,
        baseX: x,
        baseZ: z,
        baseY: CITY_BASE_Y + cityLift(city, biomeMap),
        selected: selectedId === city.id,
      };
      const arr = groups.get(prefabId);
      if (arr) arr.push(entry);
      else groups.set(prefabId, [entry]);
    }
    return Array.from(groups.entries()).map(([prefabId, entries]) => ({ prefabId, entries }));
  }, [cities, selectedId, biomeMap]);

  return (
    <>
      {batches.map(({ prefabId, entries }) => (
        <VoxelBuildingBatch
          key={`batch-${prefabId}`}
          prefabId={prefabId}
          buildings={entries}
          onClick={(cityId) => {
            const node = cities.find((c) => c.id === cityId);
            if (node) onClick?.(node);
          }}
        />
      ))}
      {cities.map((city) => (
        <CityOverlays
          key={`city-${city.id}`}
          node={city}
          selected={selectedId === city.id}
          lift={cityLift(city, biomeMap)}
        />
      ))}
      {completionVfx.map((vfx) => (
        <CompletionRing key={`vfx-${vfx.id}`} x={vfx.x} z={vfx.z} buildingName={vfx.buildingName} />
      ))}
    </>
  );
}

/** Per-city overlays — HP bar, influence ring, moat, selection ring. */
interface CityOverlaysProps {
  readonly node: SceneNode;
  readonly selected: boolean;
  readonly lift: number;
}

function CityOverlays({ node, selected, lift }: CityOverlaysProps) {
  const [x, , z] = node.transform.position;
  const color = node.visual?.material?.color ?? '#cccccc';
  const influenceRadius = (node.props?.influenceRadius as number | undefined) ?? 2;
  const ringRadius = HEX_SIZE * (influenceRadius * 1.5 + 0.5);
  const hp = (node.props?.hp as number) ?? 30;
  const hpMax = (node.props?.hpMax as number) ?? 30;
  const completedBuildings = (node.props?.completedBuildings as string[] | undefined) ?? [];
  const hasMoat = completedBuildings.includes('moat');

  const hpPct = hpMax > 0 ? Math.max(0, hp / hpMax) : 0;
  const barW = 0.5;
  const barH = 0.06;
  const fillColor = hpPct > 0.5 ? '#44cc44' : hpPct > 0.25 ? '#ccaa44' : '#cc4444';

  const ringGeometry = useMemo(
    () => new THREE.RingGeometry(ringRadius - 0.05, ringRadius, 64),
    [ringRadius],
  );
  const moatGeometry = useMemo(() => new THREE.TorusGeometry(1.0, 0.1, 8, 32), []);
  const selectionGeometry = useMemo(() => new THREE.RingGeometry(0.7, 0.78, 48), []);

  return (
    <group position={[x, lift, z]}>
      {/* HP bar above building */}
      <mesh position={[0, HEX_HEIGHT / 2 + 0.5, 0]}>
        <boxGeometry args={[barW, barH, 0.02]} />
        <meshBasicMaterial color="#333333" transparent opacity={0.7} />
      </mesh>
      <mesh position={[-(barW / 2) * (1 - hpPct), HEX_HEIGHT / 2 + 0.5, 0.001]}>
        <boxGeometry args={[barW * hpPct, barH, 0.025]} />
        <meshBasicMaterial color={fillColor} />
      </mesh>
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, HEX_HEIGHT / 2 + 0.01, 0]}
        geometry={ringGeometry}
      >
        <meshBasicMaterial color={color} transparent opacity={0.32} side={THREE.DoubleSide} />
      </mesh>
      {/* Moat ring — water-filled defensive trench */}
      {hasMoat && (
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, HEX_HEIGHT / 2 + 0.005, 0]}
          geometry={moatGeometry}
          renderOrder={RenderLayer.Infrastructure}
        >
          <meshStandardMaterial
            color="#225588"
            roughness={0.3}
            metalness={0.2}
            transparent
            opacity={0.7}
            depthWrite={false}
          />
        </mesh>
      )}
      {selected && (
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, HEX_HEIGHT / 2 + 0.02, 0]}
          geometry={selectionGeometry}
        >
          <meshBasicMaterial color="#ffd966" side={THREE.DoubleSide} />
        </mesh>
      )}
    </group>
  );
}

// Expanding ring particle burst when a building completes construction.
function CompletionRing({ x, z, buildingName }: { x: number; z: number; buildingName: string }) {
  const ringRef = useRef<THREE.Mesh>(null);
  const textRef = useRef<THREE.Sprite>(null);

  useFrame((_, delta) => {
    const ring = ringRef.current;
    if (ring) {
      ring.scale.x += delta * 3;
      ring.scale.y += delta * 3;
      const mat = ring.material as THREE.MeshBasicMaterial;
      mat.opacity = Math.max(0, mat.opacity - delta * 1.2);
    }
    const text = textRef.current;
    if (text) {
      text.position.y += delta * 1.2;
      const mat = text.material as THREE.SpriteMaterial;
      mat.opacity = Math.max(0, mat.opacity - delta * 1.2);
    }
  });

  const labelCanvas = useMemo(() => {
    const c = document.createElement('canvas');
    c.width = 128;
    c.height = 32;
    const ctx = c.getContext('2d')!;
    ctx.font = 'bold 12px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffcc44';
    ctx.fillText(`+${buildingName}`, 64, 16);
    return new THREE.CanvasTexture(c);
  }, [buildingName]);

  return (
    <group position={[x, HEX_HEIGHT / 2 + 0.3, z]}>
      <mesh ref={ringRef} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.3, 0.35, 32]} />
        <meshBasicMaterial
          color="#ffcc44"
          side={THREE.DoubleSide}
          transparent
          opacity={0.8}
          depthTest={false}
        />
      </mesh>
      <sprite ref={textRef} position={[0, 0, 0]} scale={[2.5, 0.6, 1]}>
        <spriteMaterial map={labelCanvas} transparent opacity={0.9} depthTest={false} />
      </sprite>
    </group>
  );
}
